/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import StatusOptionList from '../../../main/widgets/StatusOptionList';
import TrackingEventTypeOptionList from '../../../main/widgets/TrackingEventTypeOptionList';
import LocationOptionList from '../../../main/widgets/LocationOptionList';
import PermissionGate from '../../../main/components/PermissionGate';
import { PERMISSIONS } from '../../../main/context/AuthContext';
import OmrSymbol from '../../../main/components/OmrSymbol';
import {
  previewChargeableWeightKg,
  previewVolumetricWeightKg,
} from '../../../main/API/shipmentPiece';

export default function ShipmentPieceEditModal({
  open,
  piece,
  form,
  onFormChange,
  eventTypeOpt,
  onEventTypeChange,
  location,
  onLocationChange,
  fieldErrors = {},
  canUpdate,
  canListEventTypes,
  saving,
  onClose,
  onSave,
}) {
  const { t } = useTranslation('ui');

  const editVolumetricPreview = useMemo(() => {
    const dimsFilled = [form.width_cm, form.length_cm, form.height_cm].filter(
      (v) => v != null && v !== ''
    ).length;
    if (dimsFilled !== 3) return null;
    return previewVolumetricWeightKg(form.width_cm, form.length_cm, form.height_cm);
  }, [form.width_cm, form.length_cm, form.height_cm]);

  const editChargeablePreview = useMemo(
    () =>
      previewChargeableWeightKg(
        form.weight_kg,
        form.volumetric_weight_kg !== '' && form.volumetric_weight_kg != null
          ? form.volumetric_weight_kg
          : editVolumetricPreview
      ),
    [form.weight_kg, form.volumetric_weight_kg, editVolumetricPreview]
  );

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog variant="outlined" sx={{ maxWidth: 600, width: '100%' }} role="dialog">
        <DialogTitle>
          {(piece && piece.human_label) || (piece ? `#${piece.id}` : '')}
        </DialogTitle>
        <ModalClose onClick={onClose} />
        <Divider />
        <DialogContent>
          <Typography level="body-xs" sx={{ fontFamily: 'monospace', color: 'text.tertiary', mb: 1 }}>
            {piece && piece.barcode}
            {piece && piece.barcode ? ` · ${t('shipmentPieces.sequenceBarcodeHint')}` : ''}
          </Typography>
          <Stack spacing={1.5}>
            <Box>
              <Typography level="body-xs" sx={{ mb: 0.5 }}>
                {t('shipmentPieces.sequence')}
              </Typography>
              <Input
                type="number"
                value={form.sequence || ''}
                onChange={(e) => onFormChange({ ...form, sequence: e.target.value })}
                disabled={!canUpdate}
                color={fieldErrors.sequence ? 'danger' : 'neutral'}
              />
              {fieldErrors.sequence ? (
                <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                  {fieldErrors.sequence}
                </Typography>
              ) : (
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                  {t('shipmentPieces.sequenceEditHint')}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography level="body-xs" sx={{ mb: 0.5 }}>
                {t('actions.description')}
              </Typography>
              <Textarea
                minRows={2}
                value={form.description || ''}
                onChange={(e) => onFormChange({ ...form, description: e.target.value })}
                disabled={!canUpdate}
              />
            </Box>
            <Grid container spacing={1}>
              <Grid xs={12} sm={6}>
                <Typography level="body-xs" sx={{ mb: 0.5 }}>
                  {t('shipmentPieces.weight')}
                </Typography>
                <Input
                  type="number"
                  value={form.weight_kg || ''}
                  onChange={(e) => onFormChange({ ...form, weight_kg: e.target.value })}
                  disabled={!canUpdate}
                  color={fieldErrors.weight_kg ? 'danger' : 'neutral'}
                />
                {fieldErrors.weight_kg && (
                  <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                    {fieldErrors.weight_kg}
                  </Typography>
                )}
              </Grid>
              <Grid xs={12} sm={6}>
                <Typography level="body-xs" sx={{ mb: 0.5 }}>
                  {t('shipmentPieces.dimensions')}
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <Input
                    size="sm"
                    placeholder="W"
                    type="number"
                    value={form.width_cm || ''}
                    onChange={(e) => onFormChange({ ...form, width_cm: e.target.value })}
                    disabled={!canUpdate}
                  />
                  <Input
                    size="sm"
                    placeholder="L"
                    type="number"
                    value={form.length_cm || ''}
                    onChange={(e) => onFormChange({ ...form, length_cm: e.target.value })}
                    disabled={!canUpdate}
                  />
                  <Input
                    size="sm"
                    placeholder="H"
                    type="number"
                    value={form.height_cm || ''}
                    onChange={(e) => onFormChange({ ...form, height_cm: e.target.value })}
                    disabled={!canUpdate}
                  />
                </Stack>
                {fieldErrors.dimensions && (
                  <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                    {fieldErrors.dimensions}
                  </Typography>
                )}
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid xs={12} sm={6}>
                <Typography level="body-xs" sx={{ mb: 0.5 }}>
                  {t('shipmentPieces.volumetricWeightOverride')}
                </Typography>
                <Input
                  type="number"
                  value={form.volumetric_weight_kg || ''}
                  onChange={(e) =>
                    onFormChange({ ...form, volumetric_weight_kg: e.target.value })
                  }
                  disabled={!canUpdate}
                  placeholder={
                    editVolumetricPreview != null
                      ? `auto ≈ ${editVolumetricPreview.toFixed(3)}`
                      : ''
                  }
                />
              </Grid>
              <Grid xs={12} sm={6}>
                <Typography level="body-xs" sx={{ mb: 0.5 }}>
                  {t('shipmentPieces.chargeableWeight')}
                </Typography>
                <Input
                  readOnly
                  value={
                    piece && piece.chargeable_weight_kg != null && piece.chargeable_weight_kg !== ''
                      ? `${piece.chargeable_weight_kg} kg`
                      : editChargeablePreview != null
                        ? `${editChargeablePreview.toFixed(3)} kg (${t('shipmentPieces.chargeablePreviewHint')})`
                        : '—'
                  }
                />
              </Grid>
            </Grid>
            <StatusOptionList
              status={form.status}
              setStatus={(v) => onFormChange({ ...form, status: v })}
              allowEmpty
              disabled={!canUpdate}
              label={t('shipmentPieces.status')}
            />
            {canListEventTypes && (
              <TrackingEventTypeOptionList
                label={t('shipmentPieces.eventType')}
                required={false}
                disabled={!canUpdate}
                defaultValue={eventTypeOpt}
                onChange={(_, v) => {
                  onEventTypeChange(v != null ? v : null);
                  onFormChange({ ...form, event_type: (v && v.code) || '' });
                }}
              />
            )}
            <LocationOptionList
              defaultValue={location}
              onChange={(_, v) => onLocationChange(v != null ? v : null)}
              disabled={!canUpdate}
              label={t('shipmentPieces.location')}
            />
            <Grid container spacing={1}>
              <Grid xs={12} sm={6}>
                <Typography level="body-xs" sx={{ mb: 0.5 }}>
                  {t('shipmentPieces.productCategoryCode')}
                </Typography>
                <Input
                  value={form.product_category_code || ''}
                  onChange={(e) =>
                    onFormChange({ ...form, product_category_code: e.target.value })
                  }
                  disabled={!canUpdate}
                />
              </Grid>
              <Grid xs={12} sm={6}>
                <Typography level="body-xs" sx={{ mb: 0.5 }}>
                  {t('shipmentPieces.meterPriceOverride')}
                </Typography>
                <Input
                  type="number"
                  value={form.meter_price_override || ''}
                  onChange={(e) =>
                    onFormChange({ ...form, meter_price_override: e.target.value })
                  }
                  disabled={!canUpdate}
                  endDecorator={<OmrSymbol height={14} />}
                />
              </Grid>
            </Grid>
            <Box>
              <Typography level="body-xs" sx={{ mb: 0.5 }}>
                {t('shipmentPieces.extraCost')}
              </Typography>
              <Input
                type="number"
                value={form.extra_cost || ''}
                onChange={(e) => onFormChange({ ...form, extra_cost: e.target.value })}
                disabled={!canUpdate}
                endDecorator={<OmrSymbol height={14} />}
              />
              {piece && piece.price != null && piece.price !== '' && (
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                  {t('shipmentPieces.piecePrice')}: {piece.price}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography level="body-xs" sx={{ mb: 0.5 }}>
                {t('actions.notes')}
              </Typography>
              <Textarea
                minRows={2}
                value={form.note || ''}
                onChange={(e) => onFormChange({ ...form, note: e.target.value })}
                disabled={!canUpdate}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="plain" color="neutral" onClick={onClose} disabled={saving}>
            {t('actions.cancel')}
          </Button>
          <PermissionGate permission={PERMISSIONS.SHIPMENT_PIECES_UPDATE}>
            <Button color="primary" onClick={onSave} loading={saving}>
              {t('actions.save')}
            </Button>
          </PermissionGate>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
